import { Alert, Button, Card, Form, Input, Space, message as antdMessage } from "antd";
import { useEffect, useState } from "react";
import { Select as ZhSelect, Upload as ZhUpload, type UploadRequestOptions } from "zhihao-ui-react";

import request from "@/api";
import { BANNER_POS } from "@/meta/manageCenter/banner";
import { ADVERT_GROUP_TYPE, CARRIERS_FEATURE_PAGE_TYPE, OWNER_FEATURE_PAGE_TYPE, YES_OR_NO } from "@zhihaoscm/meta";

export interface BannerFormValues {
    pos: number;
    groupType?: number;
    type: number;
    value?: string;
    fileId?: number;
    sort?: number;
    topFlag?: number;
    activityTopTitle?: string;
}

interface BannerDetailResponse {
    banner?: BannerFormValues;
    file?: {
        path?: string;
    };
}

interface ProductSelectorItem {
    id: number | string;
    title?: string;
    productTypeName?: string;
    productTypeSpec?: string | number;
}

interface ProductTypeSelectorItem {
    id: number | string;
    name?: string;
    area?: string;
}

interface RouteSelectorItem {
    id: number | string;
    source?: string;
    destination?: string;
    sourceFullName?: string;
    destinationFullName?: string;
}

interface AdvertisementSelectorItem {
    advert?: {
        id: number | string;
        title?: string;
    };
}

interface InformationSelectorItem {
    id: number | string;
    title?: string;
}

interface CampaignSelectorItem {
    code: string;
    title?: string;
}

export interface BannerSelectorOption {
    id: number | string;
    label: string;
}

function isBannerDetailResponse(result: BannerFormValues | BannerDetailResponse): result is BannerDetailResponse {
    return "banner" in result;
}

export interface BannerFormPageProps {
    id?: number;
    pos: number;
    groupType?: number;
    sort: number;
    loadDetail?: (id: number) => Promise<BannerFormValues | BannerDetailResponse>;
    loadProductOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    loadProductTypeOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    loadRouteOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    loadAdPageOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    loadInformationOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    loadCampaignOptions?: (keyword: string) => Promise<BannerSelectorOption[]>;
    uploadImage?: (file: Blob) => Promise<number>;
    create?: (values: BannerFormValues) => Promise<unknown>;
    update?: (id: number, values: BannerFormValues) => Promise<unknown>;
    onCancel?: () => void;
    onSaved?: () => void;
}

/** 复用 Vue 移动端轮播图详情接口。 */
export async function loadBannerDetail(id: number): Promise<BannerFormValues | BannerDetailResponse> {
    const response = await request.service.get<BannerFormValues | BannerDetailResponse>(
        `/dataService/admin/banner/${id}`
    );
    return response.data;
}

/** 复用 Vue 移动端轮播图新增接口。 */
export async function createBanner(values: BannerFormValues): Promise<unknown> {
    const response = await request.service.post("/dataService/admin/banner", values);
    return response.data;
}

/** 复用 Vue 移动端轮播图修改接口。 */
export async function updateBanner(id: number, values: BannerFormValues): Promise<unknown> {
    const response = await request.service.put(`/dataService/admin/banner/${id}`, values);
    return response.data;
}

/** 复用 Vue 图片上传接口并返回文件 ID。 */
export async function uploadBannerImage(file: Blob): Promise<number> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await request.service.post<{ id: number }>("/dataService/admin/file/upload", formData, {
        timeout: 0
    });
    return response.data.id;
}

/** 复用 Vue 商品远程选择接口，并将商品结果转换为下拉选项。 */
export async function loadBannerProductOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<{ list: ProductSelectorItem[] }>(
        "/admin/product-consignment/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.title, item.productTypeName && `${item.productTypeName}(${item.productTypeSpec ?? ""})`, item.id]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 砂石指数远程选择接口，并转换为表单下拉选项。 */
export async function loadBannerProductTypeOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<{ list: ProductTypeSelectorItem[] }>(
        "/dataService/admin/product-type/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.name, item.area].filter(Boolean).join(" | ")
    }));
}

/** 复用 Vue 运价指数远程选择接口，并转换为航线下拉选项。 */
export async function loadBannerRouteOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<{ list: RouteSelectorItem[] }>(
        "/dataService/admin/ship/route/paging-selector",
        { params: { shipRouteName: keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [
            item.source && item.destination ? `${item.source}-${item.destination}` : undefined,
            item.sourceFullName && item.destinationFullName
                ? `${item.sourceFullName}-${item.destinationFullName}`
                : undefined
        ]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 小程序广告页远程选择接口，并固定 classify=2。 */
export async function loadBannerAdPageOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<{ list: AdvertisementSelectorItem[] }>(
        "/dataService/admin/advert/paging",
        { params: { key: keyword, classify: 2, page: 1, size: 50 } }
    );
    return (response.data.list || [])
        .filter(item => item.advert?.id != null)
        .map(item => ({ id: item.advert!.id, label: `${item.advert?.title || ""} | ${item.advert!.id}` }));
}

/** 复用 Vue 资讯远程选择接口，并转换为资讯下拉选项。 */
export async function loadBannerInformationOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<InformationSelectorItem[]>(
        `/dataService/admin/information/selector?title=${encodeURIComponent(keyword)}`
    );
    return (response.data || []).map(item => ({ id: item.id, label: item.title || String(item.id) }));
}

/** 复用 Vue 活动远程选择接口，并将活动编码作为提交值。 */
export async function loadBannerCampaignOptions(keyword: string): Promise<BannerSelectorOption[]> {
    const response = await request.service.get<CampaignSelectorItem[]>("/admin/campaign/selector", {
        params: { keyword }
    });
    return (response.data || []).map(item => ({ id: item.code, label: `${item.title || ""} | ${item.code}` }));
}

const initialValues: BannerFormValues = { pos: 1, type: 1, value: "" };
const jumpTypes = [
    [1, "商品"],
    [2, "砂价"],
    [3, "运价"],
    [4, "广告页"],
    [5, "资讯"],
    [6, "功能页"],
    [7, "活动"]
] as const;

/** 按 Vue 广告端位和分组规则过滤可选跳转类型。 */
function getAvailableJumpTypes(pos: number, groupType?: number) {
    if (pos === BANNER_POS.CARRIER.code) {
        // 链云船务端位不支持商品和活动跳转。
        return jumpTypes.filter(([code]) => code !== 1 && code !== 7);
    }
    if (groupType !== ADVERT_GROUP_TYPE.HOMEPAGE_ACTIVITY.code) {
        // 链云砂石非首页活动推荐分组不支持活动跳转。
        return jumpTypes.filter(([code]) => code !== 7);
    }
    return jumpTypes;
}

/** 移动端广告新增/修改基础表单，先覆盖 Vue 图片和跳转字段。 */
export function BannerFormPage({
    id,
    pos,
    groupType,
    sort,
    loadDetail = loadBannerDetail,
    loadProductOptions = loadBannerProductOptions,
    loadProductTypeOptions = loadBannerProductTypeOptions,
    loadRouteOptions = loadBannerRouteOptions,
    loadAdPageOptions = loadBannerAdPageOptions,
    loadInformationOptions = loadBannerInformationOptions,
    loadCampaignOptions = loadBannerCampaignOptions,
    uploadImage = uploadBannerImage,
    create = createBanner,
    update = updateBanner,
    onCancel,
    onSaved
}: BannerFormPageProps) {
    const [values, setValues] = useState<BannerFormValues>({ ...initialValues, pos, groupType, sort });
    const [loading, setLoading] = useState(id != null);
    const [productOptions, setProductOptions] = useState<BannerSelectorOption[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<BannerSelectorOption[]>([]);
    const [routeOptions, setRouteOptions] = useState<BannerSelectorOption[]>([]);
    const [adPageOptions, setAdPageOptions] = useState<BannerSelectorOption[]>([]);
    const [informationOptions, setInformationOptions] = useState<BannerSelectorOption[]>([]);
    const [campaignOptions, setCampaignOptions] = useState<BannerSelectorOption[]>([]);
    const [previewPath, setPreviewPath] = useState("");
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const featureTypeOptions =
        pos === BANNER_POS.CARRIER.code ? CARRIERS_FEATURE_PAGE_TYPE.options : OWNER_FEATURE_PAGE_TYPE.options;

    useEffect(() => {
        return () => {
            // 及时释放替换图片时创建的本地预览地址，避免反复编辑造成内存滞留。
            if (previewPath.startsWith("blob:") && typeof URL.revokeObjectURL === "function") {
                URL.revokeObjectURL(previewPath);
            }
        };
    }, [previewPath]);

    /** 按 Vue 的远程选择行为查询商品候选项。 */
    const searchProducts = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductOptions([]);
            return;
        }
        try {
            setProductOptions(await loadProductOptions(keyword));
        } catch {
            setProductOptions([]);
            antdMessage.error("商品搜索失败");
        }
    };

    /** 按 Vue 的远程选择行为查询砂石指数候选项。 */
    const searchProductTypes = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductTypeOptions([]);
            return;
        }
        try {
            setProductTypeOptions(await loadProductTypeOptions(keyword));
        } catch {
            setProductTypeOptions([]);
            antdMessage.error("砂石指数搜索失败");
        }
    };

    /** 按 Vue 的远程选择行为查询运价航线候选项。 */
    const searchRoutes = async (keyword: string) => {
        if (!keyword.trim()) {
            setRouteOptions([]);
            return;
        }
        try {
            setRouteOptions(await loadRouteOptions(keyword));
        } catch {
            setRouteOptions([]);
            antdMessage.error("运价指数搜索失败");
        }
    };

    /** 按 Vue 的远程选择行为查询小程序广告页候选项。 */
    const searchAdPages = async (keyword: string) => {
        if (!keyword.trim()) {
            setAdPageOptions([]);
            return;
        }
        try {
            setAdPageOptions(await loadAdPageOptions(keyword));
        } catch {
            setAdPageOptions([]);
            antdMessage.error("广告页搜索失败");
        }
    };

    /** 按 Vue 的远程选择行为查询资讯候选项。 */
    const searchInformation = async (keyword: string) => {
        if (!keyword.trim()) {
            setInformationOptions([]);
            return;
        }
        try {
            setInformationOptions(await loadInformationOptions(keyword));
        } catch {
            setInformationOptions([]);
            antdMessage.error("资讯搜索失败");
        }
    };

    /** 按 Vue 的远程选择行为查询活动候选项。 */
    const searchCampaigns = async (keyword: string) => {
        if (!keyword.trim()) {
            setCampaignOptions([]);
            return;
        }
        try {
            setCampaignOptions(await loadCampaignOptions(keyword));
        } catch {
            setCampaignOptions([]);
            antdMessage.error("活动搜索失败");
        }
    };

    /** 对齐 Vue UploadImg 的图片类型限制并回填上传后的文件 ID。 */
    const uploadBannerFile = async (file: File) => {
        try {
            const fileId = await uploadImage(file);
            setValues(current => ({ ...current, fileId }));
            // 浏览器本地地址仅用于上传后立即预览，服务端仍只保存 fileId。
            setPreviewPath(
                typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : `uploaded://${file.name}`
            );
            return fileId;
        } catch {
            setFeedbackMessage("轮播图图片上传失败");
            antdMessage.error("轮播图图片上传失败");
            throw new Error("轮播图图片上传失败");
        }
    };

    /** 仅接受旧版 UploadImg 允许的 JPEG/PNG，错误文案在选择文件时立即反馈。 */
    const validateBannerFile = (file: File) => {
        const valid = ["image/jpeg", "image/png"].includes(file.type);
        if (!valid) antdMessage.error("仅支持 JPG/PNG 图片");
        return valid;
    };

    /** 使用组件库 Upload 的自定义请求通道，避免在页面上暴露原生文件控件。 */
    const requestBannerUpload = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const fileId = await uploadBannerFile(file as File);
            onSuccess({ id: fileId });
        } catch (error) {
            onError(error instanceof Error ? error : new Error("轮播图图片上传失败"));
        }
    };

    useEffect(() => {
        if (id == null) {
            setLoading(false);
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                const detail: BannerFormValues = isBannerDetailResponse(result)
                    ? result.banner || initialValues
                    : result;
                setValues({ ...detail, pos, groupType, sort });
                setPreviewPath(isBannerDetailResponse(result) ? result.file?.path || "" : "");
                // Vue 编辑态会先把已有远程值加入候选，保证下拉框能显示可读名称。
                const remoteLoaders: Record<number, (keyword: string) => Promise<BannerSelectorOption[]>> = {
                    1: loadProductOptions,
                    2: loadProductTypeOptions,
                    3: loadRouteOptions,
                    4: loadAdPageOptions,
                    5: loadInformationOptions,
                    7: loadCampaignOptions
                };
                const remoteSetters: Record<number, (options: BannerSelectorOption[]) => void> = {
                    1: setProductOptions,
                    2: setProductTypeOptions,
                    3: setRouteOptions,
                    4: setAdPageOptions,
                    5: setInformationOptions,
                    7: setCampaignOptions
                };
                const loader = detail.value ? remoteLoaders[detail.type] : undefined;
                const setter = remoteSetters[detail.type];
                if (loader && setter && detail.value) {
                    void loader(detail.value)
                        .then(options => {
                            if (!disposed) setter(options);
                        })
                        .catch(() => {
                            if (!disposed) antdMessage.error("广告跳转内容加载失败");
                        });
                }
                setLoading(false);
            })
            .catch(() => {
                if (!disposed) {
                    antdMessage.error("轮播图详情加载失败");
                    setLoading(false);
                }
            });
        return () => {
            disposed = true;
        };
    }, [
        groupType,
        id,
        loadAdPageOptions,
        loadCampaignOptions,
        loadDetail,
        loadInformationOptions,
        loadProductOptions,
        loadProductTypeOptions,
        loadRouteOptions,
        pos,
        sort
    ]);

    const submit = async () => {
        setFeedbackMessage("");
        if (!values.fileId) {
            antdMessage.warning("请上传图片");
            return;
        }
        if ([1, 2, 3].includes(values.type) && !values.value?.trim()) {
            antdMessage.warning("请选择跳转内容");
            return;
        }
        const payload: BannerFormValues = {
            pos,
            groupType,
            type: values.type,
            value: values.value,
            fileId: values.fileId,
            sort,
            ...(values.type === 7 ? { topFlag: values.topFlag, activityTopTitle: values.activityTopTitle } : {})
        };
        try {
            if (id == null) await create(payload);
            else await update(id, payload);
            setFeedbackMessage("轮播图保存成功");
            antdMessage.success("轮播图保存成功");
            onSaved?.();
        } catch {
            setFeedbackMessage("轮播图保存失败");
            antdMessage.error("轮播图保存失败");
        }
    };

    if (loading) return <div role="status">轮播图加载中...</div>;

    /** 将组件选中值统一转为旧接口使用的字符串。 */
    const updateSelectedValue = (value: string | number | undefined) => {
        setValues(current => ({ ...current, value: value == null ? "" : String(value) }));
    };

    const selectOptions = (options: BannerSelectorOption[]) =>
        options.map(option => ({ label: option.label, value: String(option.id) }));

    return (
        <section aria-label="banner-form-page">
            <Card>
                {feedbackMessage ? (
                    <Alert
                        className="mb-4"
                        message={feedbackMessage}
                        role="alert"
                        type={feedbackMessage.endsWith("成功") ? "success" : "error"}
                    />
                ) : null}
                <Form labelAlign="left" labelCol={{ flex: values.type === 7 ? "116px" : "80px" }}>
                    <Form.Item label="跳转类型" required>
                        <div
                            aria-label="跳转类型"
                            className="jump-type"
                            role="radiogroup"
                            style={{ display: "flex", flexWrap: "wrap", gap: 24, width: "100%" }}
                        >
                            {getAvailableJumpTypes(pos, groupType).map(([code, label]) => {
                                const checked = values.type === code;
                                return (
                                    <button
                                        key={code}
                                        aria-checked={checked}
                                        aria-label={label}
                                        role="radio"
                                        style={{
                                            alignItems: "center",
                                            background: "transparent",
                                            border: 0,
                                            color: "inherit",
                                            cursor: "pointer",
                                            display: "flex",
                                            gap: 8,
                                            padding: 0
                                        }}
                                        type="button"
                                        onClick={() => setValues(current => ({ ...current, type: code, value: "" }))}
                                    >
                                        <i
                                            aria-hidden="true"
                                            className={`iconfont ${
                                                checked
                                                    ? "icon-danxuankuangxuanzhong active-radio"
                                                    : "icon-danxuankuang"
                                            }`}
                                            style={{ color: checked ? "var(--ant-color-primary)" : "#d9d9d9" }}
                                        />
                                        <span>{label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </Form.Item>
                    {values.type === 1 ? (
                        <Form.Item label="商品" required>
                            <ZhSelect
                                allowClear
                                aria-label="商品"
                                filterOption={false}
                                options={selectOptions(productOptions)}
                                placeholder="请搜索商品名称或商品id选择"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                                onSearch={keyword => void searchProducts(keyword)}
                            />
                        </Form.Item>
                    ) : values.type === 2 ? (
                        <Form.Item label="砂石指数" required>
                            <ZhSelect
                                allowClear
                                aria-label="砂石指数"
                                filterOption={false}
                                options={selectOptions(productTypeOptions)}
                                placeholder="请搜索品类名称或品类代码选择"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                                onSearch={keyword => void searchProductTypes(keyword)}
                            />
                        </Form.Item>
                    ) : values.type === 3 ? (
                        <Form.Item label="运价指数" required>
                            <ZhSelect
                                allowClear
                                aria-label="运价指数"
                                filterOption={false}
                                options={selectOptions(routeOptions)}
                                placeholder="请搜索码头名称或航线代码选择"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                                onSearch={keyword => void searchRoutes(keyword)}
                            />
                        </Form.Item>
                    ) : values.type === 4 ? (
                        <Form.Item label="广告页">
                            <ZhSelect
                                allowClear
                                aria-label="广告页"
                                filterOption={false}
                                options={selectOptions(adPageOptions)}
                                placeholder="请搜索广告页标题或编号选择"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                                onSearch={keyword => void searchAdPages(keyword)}
                            />
                        </Form.Item>
                    ) : values.type === 5 ? (
                        <Form.Item label="资讯" required>
                            <ZhSelect
                                allowClear
                                aria-label="资讯"
                                filterOption={false}
                                options={selectOptions(informationOptions)}
                                placeholder="请搜索资讯标题"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                                onSearch={keyword => void searchInformation(keyword)}
                            />
                        </Form.Item>
                    ) : values.type === 6 ? (
                        <Form.Item label="功能页" required>
                            <ZhSelect
                                allowClear
                                aria-label="功能页"
                                optionFilterProp="label"
                                options={featureTypeOptions.map(option => ({
                                    label: option.label,
                                    value: String(option.value)
                                }))}
                                placeholder="请搜索功能页"
                                showSearch
                                value={values.value || undefined}
                                onChange={updateSelectedValue}
                            />
                        </Form.Item>
                    ) : values.type === 7 ? (
                        <>
                            <Form.Item label="活动">
                                <ZhSelect
                                    allowClear
                                    aria-label="活动"
                                    filterOption={false}
                                    options={selectOptions(campaignOptions)}
                                    placeholder="请搜索活动名称或活动id"
                                    showSearch
                                    value={values.value || undefined}
                                    onChange={updateSelectedValue}
                                    onSearch={keyword => void searchCampaigns(keyword)}
                                />
                            </Form.Item>
                            <Form.Item label="是否悬浮置顶">
                                <Space className="w-full">
                                    <ZhSelect
                                        allowClear
                                        aria-label="是否悬浮置顶"
                                        options={YES_OR_NO.options}
                                        placeholder="请选择是否悬浮置顶"
                                        style={{ width: 180 }}
                                        value={values.topFlag}
                                        onChange={value =>
                                            setValues(current => ({
                                                ...current,
                                                topFlag: value == null ? undefined : Number(value)
                                            }))
                                        }
                                    />
                                    {values.topFlag ? (
                                        <Input
                                            aria-label="活动置顶标题"
                                            maxLength={100}
                                            placeholder="请输入活动置顶标题"
                                            value={values.activityTopTitle || ""}
                                            onChange={event =>
                                                setValues(current => ({
                                                    ...current,
                                                    activityTopTitle: event.target.value
                                                }))
                                            }
                                        />
                                    ) : null}
                                </Space>
                            </Form.Item>
                        </>
                    ) : (
                        <Form.Item label="跳转值">
                            <Input
                                aria-label="跳转值"
                                placeholder="请输入跳转值"
                                value={values.value || ""}
                                onChange={event => updateSelectedValue(event.target.value)}
                            />
                        </Form.Item>
                    )}
                    <Form.Item required>
                        <ZhUpload
                            accept="image/jpeg,image/png"
                            action="#"
                            aria-label="上传轮播图图片"
                            beforeUpload={file => validateBannerFile(file as File)}
                            className="w-full"
                            httpRequest={requestBannerUpload}
                            limit={1}
                            showFileList={false}
                        >
                            <div
                                className="banner-image-upload-panel"
                                data-testid="banner-image-upload-panel"
                                style={{
                                    alignItems: "center",
                                    background: "rgba(232, 242, 255, 0.2)",
                                    border: "1px dashed #cbd8f2",
                                    borderRadius: 6,
                                    color: "var(--ant-color-primary)",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    height: 240,
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    position: "relative",
                                    width: "100%"
                                }}
                            >
                                {values.fileId ? (
                                    <>
                                        <img
                                            alt="轮播图预览"
                                            src={previewPath || undefined}
                                            style={{ height: "100%", objectFit: "contain", width: "100%" }}
                                        />
                                        <span
                                            style={{
                                                background: "rgba(255, 255, 255, 0.88)",
                                                bottom: 0,
                                                padding: "12px 0",
                                                position: "absolute",
                                                textAlign: "center",
                                                width: "100%"
                                            }}
                                        >
                                            <i aria-hidden="true" className="iconfont icon-PictureFilled" />
                                            替换图片
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                        <span>上传图片</span>
                                    </>
                                )}
                            </div>
                        </ZhUpload>
                    </Form.Item>
                </Form>
            </Card>
            <Space className="operation-fixed-btn" data-testid="banner-form-fixed-actions" size={0}>
                <Button aria-label="取消" onClick={() => (onCancel || onSaved)?.()}>
                    取消
                </Button>
                <Button aria-label="提交" type="primary" onClick={() => void submit()}>
                    提交
                </Button>
            </Space>
        </section>
    );
}

export default BannerFormPage;
